import { useEffect, useState, useCallback } from 'react';
import PageHeader from '../../components/common/PageHeader';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { getAuditoria, getAuditoriaMeta } from '../../api/auditoria';

const PERFIL_COLOR = { admin: 'danger', fenix: 'primary', cliente: 'secondary' };

const ACAO_COLOR = {
  login: 'info', create: 'success', update: 'warning',
  delete: 'danger', confirm: 'success', recusar: 'danger',
  cancelar: 'secondary', standby: 'secondary', reagendar: 'warning',
  upload: 'primary', resultado: 'success',
};

function fmtDatetime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()} ${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}:${String(d.getSeconds()).padStart(2,'0')}`;
}

export default function Auditoria() {
  const [logs, setLogs] = useState([]);
  const [meta, setMeta] = useState({ entidades: [], acoes: [] });
  const [loading, setLoading] = useState(true);
  const [filtro, setFiltro] = useState({ entidade: '', acao: '', email: '', data_inicio: '', data_fim: '' });
  const [skip, setSkip] = useState(0);
  const LIMIT = 100;

  const load = useCallback(() => {
    setLoading(true);
    const params = { skip, limit: LIMIT };
    if (filtro.entidade) params.entidade = filtro.entidade;
    if (filtro.acao) params.acao = filtro.acao;
    if (filtro.email) params.email = filtro.email;
    if (filtro.data_inicio) params.data_inicio = filtro.data_inicio;
    if (filtro.data_fim) params.data_fim = filtro.data_fim;
    getAuditoria(params).then(setLogs).finally(() => setLoading(false));
  }, [filtro, skip]);

  useEffect(() => { getAuditoriaMeta().then(setMeta); }, []);
  useEffect(() => { load(); }, [load]);

  const setF = (k) => (e) => { setFiltro(f => ({ ...f, [k]: e.target.value })); setSkip(0); };

  const limparFiltros = () => { setFiltro({ entidade: '', acao: '', email: '', data_inicio: '', data_fim: '' }); setSkip(0); };
  const temFiltro = Object.values(filtro).some(Boolean);

  return (
    <>
      <PageHeader title="Auditoria" subtitle="Registro de ações realizadas no sistema — visível apenas para administradores" />

      <div className="card-datafit mb-4">
        <div className="p-3 border-bottom d-flex gap-2 flex-wrap align-items-end">
          <div>
            <label className="form-label small mb-1">Entidade</label>
            <select className="form-select form-select-sm" style={{ width: 160 }} value={filtro.entidade} onChange={setF('entidade')}>
              <option value="">Todas</option>
              {meta.entidades.map(e => <option key={e} value={e}>{e}</option>)}
            </select>
          </div>
          <div>
            <label className="form-label small mb-1">Ação</label>
            <select className="form-select form-select-sm" style={{ width: 140 }} value={filtro.acao} onChange={setF('acao')}>
              <option value="">Todas</option>
              {meta.acoes.map(a => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>
          <div>
            <label className="form-label small mb-1">Usuário (email)</label>
            <input type="text" className="form-control form-control-sm" style={{ width: 200 }}
              placeholder="Buscar e-mail..." value={filtro.email} onChange={setF('email')} />
          </div>
          <div>
            <label className="form-label small mb-1">De</label>
            <input type="date" className="form-control form-control-sm" value={filtro.data_inicio} onChange={setF('data_inicio')} />
          </div>
          <div>
            <label className="form-label small mb-1">Até</label>
            <input type="date" className="form-control form-control-sm" value={filtro.data_fim} onChange={setF('data_fim')} />
          </div>
          {temFiltro && (
            <button className="btn btn-sm btn-outline-secondary" onClick={limparFiltros}>
              <i className="bi bi-x me-1"></i>Limpar
            </button>
          )}
        </div>

        {loading ? (
          <div className="p-4 text-center"><LoadingSpinner /></div>
        ) : (
          <>
            <div className="table-responsive">
              <table className="table table-datafit mb-0" style={{ fontSize: '.85rem' }}>
                <thead>
                  <tr>
                    <th style={{ whiteSpace: 'nowrap' }}>Data / Hora</th>
                    <th>Usuário</th>
                    <th>Entidade</th>
                    <th>Ação</th>
                    <th>Descrição</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.length === 0 ? (
                    <tr><td colSpan={5} className="text-center text-muted py-4">Nenhum registro encontrado</td></tr>
                  ) : logs.map(l => (
                    <tr key={l.id_audit}>
                      <td style={{ whiteSpace: 'nowrap' }} className="text-muted small">
                        {fmtDatetime(l.created_at)}
                      </td>
                      <td>
                        <div className="small">{l.email_usuario || '—'}</div>
                        {l.perfil_usuario && (
                          <span className={`badge bg-${PERFIL_COLOR[l.perfil_usuario] || 'secondary'}`} style={{ fontSize: '.65rem' }}>
                            {l.perfil_usuario}
                          </span>
                        )}
                      </td>
                      <td>
                        <span className="badge bg-light text-dark border small">{l.entidade}</span>
                        {l.id_entidade && (
                          <div className="text-muted" style={{ fontSize: '.65rem' }}>{l.id_entidade.slice(0, 8)}…</div>
                        )}
                      </td>
                      <td>
                        <span className={`badge bg-${ACAO_COLOR[l.acao] || 'secondary'}`}>{l.acao}</span>
                      </td>
                      <td className="text-muted small">{l.descricao || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="p-3 border-top d-flex justify-content-between align-items-center small text-muted">
              <span>Exibindo {logs.length} de até {LIMIT} registros</span>
              <div className="d-flex gap-2">
                <button className="btn btn-sm btn-outline-secondary" disabled={skip === 0}
                  onClick={() => setSkip(s => Math.max(0, s - LIMIT))}>
                  ‹ Anterior
                </button>
                <button className="btn btn-sm btn-outline-secondary" disabled={logs.length < LIMIT}
                  onClick={() => setSkip(s => s + LIMIT)}>
                  Próximo ›
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}
